import type { Chapter } from "../events/landing";
import { formatDayHeading, groupAgendaByDay } from "./agenda-schedule";
import { chaptersFor, dayNumbers, type EventDates } from "./landing-model";
import type { AgendaItem } from "./schemas";

/**
 * The programme layout of the Agenda tab: per day a header, the highlighted
 * sessions as colour cards and every other session as a card between them.
 * Nothing is folded away: every session is listed.
 *
 * It is a different arrangement of the same sessions the schedule lists, and
 * the Agenda tab uses it only for an event that has a highlight or a day
 * title. Days are the schedule's own (`groupAgendaByDay`), so the day key that
 * names a title is the one the rest of the tab groups by.
 */

export type ProgrammeBlock =
  | { kind: "highlight"; item: AgendaItem; chapter: Chapter }
  | {
      kind: "sessions";
      /** Stable across renders: the first session's id. */
      id: string;
      /** The sessions between two highlight cards, in time order. */
      items: AgendaItem[];
    };

export type ProgrammeDay = {
  key: string;
  /** "Day 1": the place among the days of the whole schedule. */
  number: number;
  /** "Wednesday, 21 October". */
  heading: string;
  /** The organiser's one-line title for the day, if any. */
  title: string | null;
  blocks: ProgrammeBlock[];
};

function cleanTitles(
  dayTitles: Record<string, string> | null | undefined,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(dayTitles ?? {})
      .map(([key, title]) => [key, title.trim()] as const)
      .filter(([, title]) => title.length > 0),
  );
}

/**
 * Whether the Agenda tab uses the programme layout: a highlighted session, or
 * a title for any day. Without either it is the plain schedule.
 */
export function hasProgramme(
  sessions: readonly AgendaItem[],
  dayTitles: Record<string, string> | null | undefined,
): boolean {
  return (
    sessions.some((item) => item.highlight === true) ||
    Object.keys(cleanTitles(dayTitles)).length > 0
  );
}

/**
 * The programme for `items` (what the filters leave), numbered against
 * `everything` (every session with a time), so a filter never renumbers a
 * day or a chapter.
 */
export function buildProgramme(
  items: AgendaItem[],
  everything: AgendaItem[],
  timeZone: string | undefined,
  dayTitles: Record<string, string> | null | undefined,
  dates?: EventDates,
): ProgrammeDay[] {
  const titles = cleanTitles(dayTitles);
  const numbers = dayNumbers(everything, timeZone, dates);
  const chapterById = new Map(
    chaptersFor(everything).map((chapter) => [chapter.id, chapter]),
  );

  return groupAgendaByDay(items, timeZone).days.map((day) => {
    const blocks: ProgrammeBlock[] = [];
    let run: AgendaItem[] = [];
    const closeRun = () => {
      if (run.length === 0) return;
      blocks.push({ kind: "sessions", id: run[0].id, items: run });
      run = [];
    };
    for (const item of day.items) {
      const chapter = chapterById.get(item.id);
      if (chapter) {
        closeRun();
        blocks.push({ kind: "highlight", item, chapter });
      } else {
        run.push(item);
      }
    }
    closeRun();
    return {
      key: day.key,
      number: numbers.get(day.key) ?? 0,
      heading: formatDayHeading(day.key),
      title: titles[day.key] ?? null,
      blocks,
    };
  });
}

/** "Day 01": a day's label on the Agenda's day filter. */
export function dayFilterLabel(number: number): string {
  return `Day ${String(number).padStart(2, "0")}`;
}

export type DayFilterOption = {
  /** YYYY-MM-DD on the event's clock, the key the schedule groups by. */
  key: string;
  /** The day's place in the event, as in each day's "Day N" header. */
  number: number;
  label: string;
};

/**
 * One option per day that has a session, in date order, numbered the way the
 * Agenda's day headers are. Empty for an event whose sessions fall on a single
 * day, where a day filter would change nothing.
 */
export function dayFilterOptions(
  sessions: AgendaItem[],
  timeZone: string | undefined,
  dates?: EventDates,
): DayFilterOption[] {
  const numbers = dayNumbers(sessions, timeZone, dates);
  const options = groupAgendaByDay(sessions, timeZone).days.map((day) => {
    const number = numbers.get(day.key) ?? 0;
    return { key: day.key, number, label: dayFilterLabel(number) };
  });
  return options.length > 1 ? options : [];
}

/** The same text with its runs of whitespace folded, for comparing two texts. */
function flat(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

/**
 * What a highlight card says about its session, with nothing behind a fold.
 *
 * With no blurb of the organiser's own, the card's blurb is the session's
 * description cut at 300 characters, so the whole description is the lead and
 * the cut-off tail is not lost. With a blurb of their own, that is the lead and
 * the description, if it says something else, follows it.
 */
export function highlightText(
  item: AgendaItem,
  blurb: string,
): { lead: string; more: string | null } {
  const description = item.description?.trim() ?? "";
  if (!item.highlightBlurb?.trim()) {
    return { lead: description || blurb, more: null };
  }
  return {
    lead: blurb,
    more: description && flat(description) !== flat(blurb) ? description : null,
  };
}
